import { getEffectiveUserStatus } from '@modules/user/utils/getEffectiveUserStatus';
import {
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  styled,
} from '@mui/material';
import cn from 'classnames';
import { m } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { UserType } from 'src/@types/workspace-info';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { PageHeaderContainer } from 'src/components/PageHeader/PageHeaderContainer';
import { Search } from 'src/components/Search/Search';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { avatarSizes } from 'src/components/base/CPAvatar/avatarSizes';
import { Iconify } from 'src/components/iconify';
import {
  HEADER,
  MOBILE_BREAKPOINT,
  SMALL_MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
} from 'src/config-global';
import { useWorkspaceUser } from 'src/hooks/useWorkspace';
import { useLocales } from 'src/locales';
import { useChatContext } from 'src/provider/ChatContext';
import {
  useVoiceChannelContext,
  useVoiceChannelsMembers,
} from 'src/provider/VoiceChannel';
import { useChatConversationContext } from 'src/sections/@dashboard/chat/ChatConversation/useChatConversationContext';
import { PinnedMessage } from 'src/sections/@dashboard/chat/ChatHeader/PinnedMessages';
import {
  ChatDetailsTitle,
  useChatDetailsDialog,
} from 'src/sections/@dashboard/chat/shared/ChatOverviewDialog';
import { CallAI } from '../../../../features/ActionsBar/CallAI';
import { VoiceChannelControls } from '../ChatVoiceChannel/VoiceChannelControls';
import { VoiceChannelMembersGrid } from '../ChatVoiceChannel/VoiceChannelMembersGrid';
import { ChatHeaderProps } from './types';

const HEADER_HEIGHT = 65;
const INPUT_HEIGHT = 80;

const StyledTitle = styled(Typography)(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ChatHeaderP2P = ({ conversation, onClose }: ChatHeaderProps) => {
  const [hideButtons, setHideButtons] = useState(false);
  const { conversationKey = '' } = useParams();
  const { translate } = useLocales();
  const navigate = useNavigate();

  const otherUser = useWorkspaceUser(conversation.chatPartner?.user || '');

  const { searchTerm, setSearchTerm } = useChatConversationContext();

  const { showChatDetailsDialog } = useChatDetailsDialog();

  const { isDrawer, isCompact, setCurrentActiveChat } = useChatContext();

  const { connectVoiceChannel } = useVoiceChannelContext();

  const shouldRenderButtons = conversation._id !== conversationKey;

  const { voiceMembers, isChannelActive } =
    useVoiceChannelsMembers(conversation);

  const handleShowDetails = () => {
    showChatDetailsDialog({
      conversationId: conversation._id,
      tab: ChatDetailsTitle.MEDIA,
    });
  };

  const hasVideo = useMemo(
    () => voiceMembers.some((member) => member.sharing || member.camera),
    [voiceMembers],
  );

  const goToChannel = () => {
    navigate(PATH_DASHBOARD.chat.view(conversation._id));
    if (onClose) onClose();
  };

  const handleClose = () => {
    if (onClose) onClose();
    setCurrentActiveChat('');
  };

  return (
    <PageHeaderContainer>
      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        gap={1}
        onClick={handleShowDetails}
        sx={{ cursor: 'pointer' }}
      >
        {otherUser && (
          <>
            <CPAvatar
              name={otherUser.name}
              picture={otherUser.picture}
              status={getEffectiveUserStatus(otherUser)}
            />
            <StyledTitle variant="subtitle1">{conversation?.name}</StyledTitle>
          </>
        )}
      </Stack>

      {conversation?._id && (
        <Stack direction="row" alignItems="center" gap={1.5}>
          {!hideButtons && (
            <>
              {otherUser?.type === UserType.USER && (
                <Button
                  sx={{ fontWeight: 'fontWeightRegular' }}
                  startIcon={<Iconify icon="tabler:phone" />}
                  onClick={() =>
                    connectVoiceChannel({
                      channel: conversation,
                      callOtherMembers: true,
                    })
                  }
                  disabled={isChannelActive}
                >
                  Call
                </Button>
              )}
              {otherUser?.type === UserType.AI && <CallAI />}
              <PinnedMessage channelId={conversation._id} compact={isCompact} />
            </>
          )}
          <Search
            searchTerm={searchTerm}
            onSearch={(inputValue) => setSearchTerm(inputValue)}
            clearSearch={() => setSearchTerm('')}
            onShowInput={(visibility) => setHideButtons(visibility)}
            onlyIcon={isCompact}
          />

          {isDrawer && shouldRenderButtons && (
            <>
              <Tooltip title={translate('communicate.open_on_communicate')}>
                <IconButton onClick={goToChannel}>
                  <Iconify icon="tabler:message-2-share" />
                </IconButton>
              </Tooltip>

              <IconButton onClick={handleClose}>
                <Iconify icon="tabler:x" />
              </IconButton>
            </>
          )}
        </Stack>
      )}
      {isChannelActive && otherUser?.type === UserType.USER && (
        <StyledVideoContainer className={cn({ hasVideo })}>
          <VoiceChannelMembersGrid
            participants={voiceMembers}
            conversationId={conversation._id}
          />
          <VoiceControlsContainer>
            <VoiceChannelControls isP2P floating={false} />
          </VoiceControlsContainer>
        </StyledVideoContainer>
      )}
    </PageHeaderContainer>
  );
};

const VoiceControlsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: '100%',
  height: 'fit-content',
  bottom: 0,
  margin: theme.spacing(2),
  zIndex: 1,
}));

const StyledVideoContainer = styled(m.div)(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  top: '100%',
  left: 0,
  margin: 0,
  zIndex: 10,
  background: alpha(theme.palette.common.black, 0.96),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0, 0, 1, 1),
  padding: theme.spacing(2),
  boxShadow: theme.customShadows.dialog,
  maxHeight: `calc(100dvh - ${(2 * HEADER) + HEADER_HEIGHT + INPUT_HEIGHT}px)`,
  minHeight: 'auto',
  height: 'auto',
  [theme.breakpoints.up(MOBILE_BREAKPOINT)]: {
    maxHeight: `calc(100dvh - ${HEADER + HEADER_HEIGHT + INPUT_HEIGHT}px)`,
  },
  '& .MuiGrid-root': {
    '--columns': 4,
    justifyContent: 'center',
  },
  '&.hasVideo': {
    height: 'auto',
    minHeight: 'auto',

    '& .MuiGrid-root': {
      '--columns': 2,
      justifyContent: 'center',
    },
  },
  '& .MuiAvatar-root': {
    ...avatarSizes.large,
    clipPath: undefined,
    [theme.breakpoints.up(SMALL_MOBILE_BREAKPOINT)]: {
      ...avatarSizes.xLarge,
      clipPath: undefined,
    },
    [theme.breakpoints.up(MOBILE_BREAKPOINT)]: {
      ...avatarSizes.medium,
      clipPath: undefined,
    },
    [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
      ...avatarSizes.xLarge,
      clipPath: undefined,
    },
  },
}));
